import React, { useEffect, useState } from "react";
import Table from "react-bootstrap/Table";
import { Form } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import BatchPagination from "../batch/BatchPagination";
import LoaderSpinner from "../common/Loader";
import { numOfPage } from "../../constant";
import {
    getSupplierOrders,
    updateFulfillment,
} from "../../store/actions/dropship.action";
import ShipModal from "./ShipModal";

const STATUS_FILTERS = ["All", "Pending", "Accepted", "Shipped", "Delivered", "Cancelled"];

export default function DropshipFulfillment() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { supplierOrders, supplierOrdersStatus, supplierOrdersTotalRecords } = useSelector(
        (state) => state.dropshipReducer
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [statusFilter, setStatusFilter] = useState("All");
    const [showShipModal, setShowShipModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const loader = supplierOrdersStatus === "PENDING";

    useEffect(() => {
        loadOrders();
    }, [currentPage, pageSize, statusFilter]);

    const loadOrders = () => {
        dispatch(
            getSupplierOrders({
                pageIndex: currentPage,
                pageSize: pageSize,
                fulfillmentStatus: statusFilter === "All" ? null : statusFilter,
            })
        );
    };

    const handleAccept = async (orderId) => {
        try {
            const result = await updateFulfillment({
                dropshipOrderId: orderId,
                fulfillmentStatus: "Accepted",
            });
            if (result.data?.EntityList?.[0]?.success) {
                toast.success("Order accepted.");
                loadOrders();
            } else {
                toast.error(result.data?.EntityList?.[0]?.message || "Update failed.");
            }
        } catch (err) {
            toast.error("Failed to accept order.");
        }
    };

    const handleShipClick = (order) => {
        setSelectedOrder(order);
        setShowShipModal(true);
    };

    const handleShipSuccess = () => {
        loadOrders();
    };

    const statusBadgeClass = (status) => {
        switch (status) {
            case "Pending":
                return "bg-warning";
            case "Accepted":
                return "bg-info";
            case "Shipped":
                return "bg-primary";
            case "Delivered":
                return "bg-success";
            case "Cancelled":
                return "bg-danger";
            default:
                return "bg-secondary";
        }
    };

    const totalPages = Math.ceil(supplierOrdersTotalRecords / pageSize) || 1;

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    Dropship Fulfillment
                    <div className="top_right_button_container">
                        <Button
                            className="cus-primary-transparent-btn"
                            onClick={() => navigate("/dropship-settings")}
                        >
                            Settings
                        </Button>
                    </div>
                </div>

                <div className="main_content_table incomingTbl">
                    {/* Status Filter */}
                    <div className="cus-page-search-sec" style={{ padding: "12px 16px" }}>
                        <div className="d-flex align-items-center gap-2">
                            <label htmlFor="dropshipFulfillmentStatusFilter" style={{ marginBottom: 0, marginRight: 8 }}>
                                Filter by Status:
                            </label>
                            <Form.Select
                                id="dropshipFulfillmentStatusFilter"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{ maxWidth: "200px" }}
                            >
                                {STATUS_FILTERS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    </div>

                    {loader && (
                        <div className="full-screen-spinner-overlay">
                            <LoaderSpinner />
                        </div>
                    )}

                    <div className="tableFixHead">
                        <Table className="table table-borderless">
                            <thead>
                                <tr>
                                    <th width="4%">#</th>
                                    <th width="10%">Order #</th>
                                    <th width="8%">SKU</th>
                                    <th width="13%">Product</th>
                                    <th width="11%">Dropshipper</th>
                                    <th width="14%">Ship To</th>
                                    <th width="6%">Qty</th>
                                    <th width="8%">Payout</th>
                                    <th width="8%">Status</th>
                                    <th width="8%">Date</th>
                                    <th width="10%">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loader && supplierOrders.length === 0 && (
                                    <tr>
                                        <td colSpan="11" className="text-center py-4" style={{ color: "#999" }}>
                                            No fulfillment requests{" "}
                                            {statusFilter !== "All" ? `in status "${statusFilter}"` : "yet"}.
                                        </td>
                                    </tr>
                                )}
                                {supplierOrders.map((order, index) => (
                                    <tr key={order.id}>
                                        <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                        <td>{order.orderNumber}</td>
                                        <td>{order.productSKU}</td>
                                        <td>{order.productName}</td>
                                        <td>Verified Dropshipper</td>
                                        <td style={{ fontSize: "12px" }}>
                                            <div>{order.shipToName}</div>
                                            <div style={{ color: "#666" }}>
                                                {order.shipToCity}, {order.shipToState} {order.shipToZip}
                                            </div>
                                        </td>
                                        <td>{order.quantity || "—"}</td>
                                        <td>${order.wholesaleAmount?.toFixed(2)}</td>
                                        <td>
                                            <span className={`badge ${statusBadgeClass(order.fulfillmentStatus)}`}>
                                                {order.fulfillmentStatus}
                                            </span>
                                            {order.trackingNumber && (
                                                <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>
                                                    {order.carrierName}: {order.trackingNumber}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ fontSize: "12px" }}>
                                            {order.createdDate && moment(order.createdDate).format("MM/DD/YYYY")}
                                        </td>
                                        <td>
                                            {order.fulfillmentStatus === "Pending" && (
                                                <Button
                                                    className="cus-primary-transparent-btn btn-sm"
                                                    onClick={() => handleAccept(order.id)}
                                                >
                                                    Accept
                                                </Button>
                                            )}
                                            {(order.fulfillmentStatus === "Pending" ||
                                                order.fulfillmentStatus === "Accepted") && (
                                                <Button
                                                    className="cus-seconday-bg-btn btn-sm"
                                                    onClick={() => handleShipClick(order)}
                                                >
                                                    Ship
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {supplierOrdersTotalRecords > 0 && (
                        <BatchPagination
                            gridData={supplierOrders}
                            numberOfPage={pageSize}
                            numOfPage={numOfPage}
                            loader={loader}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalRecords={supplierOrdersTotalRecords}
                            setCurrentPage={setCurrentPage}
                            setNumberOfPage={(val) => {
                                setPageSize(val);
                                setCurrentPage(1);
                            }}
                        />
                    )}
                </div>
            </div>

            {showShipModal && selectedOrder && (
                <ShipModal
                    show={showShipModal}
                    onHide={() => setShowShipModal(false)}
                    order={selectedOrder}
                    onSuccess={handleShipSuccess}
                />
            )}
        </main>
    );
}
